import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BookOpen, CalendarDays } from "lucide-react";
import { API_BASE } from "@/config/links";
import { fadeUp, revealProps, scaleIn, stagger } from "@/lib/motion";

function formatDate(value) {
  if (!value) return "";
  return new Date(`${value}T00:00:00`).toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "long",
  });
}

const StoryDemo = () => {
  const [story, setStory] = useState(null);

  useEffect(() => {
    let alive = true;
    fetch(`${API_BASE}/api/stories?limit=1`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("no story"))))
      .then((data) => {
        const latest = (data.stories || [])[0];
        if (alive && latest) setStory(latest);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  if (!story) return null;

  const storyPath = story.slug
    ? `/stories/${story.sourceKey}/${story.slug}`
    : `/stories/${story.sourceKey}`;

  return (
    <section className="relative overflow-hidden bg-night-800 py-16 md:py-24">
      <div className="glow-warm pointer-events-none absolute inset-0 opacity-60" />
      <motion.div
        {...revealProps}
        variants={stagger(0.1)}
        className="relative mx-auto grid max-w-6xl items-center gap-8 px-4 sm:px-6 md:grid-cols-[0.9fr_1.1fr] md:gap-12"
      >
        <motion.div variants={scaleIn} className="relative">
          <div className="absolute -inset-4 rounded-[2.5rem] bg-amber/10 blur-2xl" />
          <div className="relative overflow-hidden rounded-3xl border border-line bg-night-card">
            {story.videoUrl ? (
              <video
                className="aspect-video w-full bg-black"
                src={story.videoUrl}
                poster={story.coverUrl || undefined}
                controls
                playsInline
                preload="metadata"
              />
            ) : story.coverUrl ? (
              <img
                src={story.coverUrl}
                alt={story.title}
                loading="lazy"
                className="aspect-[4/3] w-full object-cover"
              />
            ) : (
              <div className="flex aspect-[4/3] w-full items-center justify-center text-dim">
                <BookOpen className="h-10 w-10" />
              </div>
            )}
          </div>
        </motion.div>

        <div>
          <motion.div variants={fadeUp} className="mb-4 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-line bg-white/[0.05] px-3 py-1 text-xs font-medium text-muted-foreground">
              <CalendarDays className="h-3.5 w-3.5" />
              {formatDate(story.date)}
            </span>
            {story.rubric ? (
              <span className="inline-flex items-center rounded-full bg-amber/15 px-3 py-1 text-xs font-medium text-amber-soft">
                {story.rubric}
              </span>
            ) : null}
          </motion.div>

          <motion.p variants={fadeUp} className="mb-2 text-[11px] uppercase tracking-[0.22em] text-amber">
            Сегодняшняя сказка
          </motion.p>
          <motion.h2
            variants={fadeUp}
            className="mb-4 font-serif text-3xl leading-tight text-cream sm:text-4xl lg:text-5xl"
          >
            {story.title}
          </motion.h2>
          {story.excerpt ? (
            <motion.p variants={fadeUp} className="mb-7 max-w-xl leading-relaxed text-muted-foreground">
              {story.excerpt}
            </motion.p>
          ) : null}

          <motion.div variants={fadeUp} className="flex flex-col gap-3 sm:flex-row">
            <Link
              to={storyPath}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-amber px-6 font-semibold text-night-900 shadow-glow transition-all duration-300 hover:bg-amber-hover hover:shadow-glow-lg"
            >
              <BookOpen className="h-5 w-5" />
              Читать
            </Link>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

export default StoryDemo;
